import { useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, useMediaQuery, useTheme } from '@mui/material';
import { FieldValue } from '../../../../../types/QueryBuilder';
import { useLanguage } from '../../../../../context/LanguageContext';
import CheckboxListPicker from './CheckboxListPicker';
import { formatSelectedCount } from './selectedCountLabel';

interface SelectionDialogEditorProps {
    ruleId: string;
    value?: string | string[] | number | null;
    values: FieldValue[];
    title: string;
    onChange: (value: string[]) => void;
}

function SelectionDialogEditor({ ruleId, value, values, title, onChange }: SelectionDialogEditorProps) {
    const { t } = useLanguage();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const committedValues = Array.isArray(value) ? value : [];
    const [open, setOpen] = useState(false);
    const [pendingValues, setPendingValues] = useState<string[]>(committedValues);

    const handleOpen = () => {
        setPendingValues(committedValues);
        setOpen(true);
    };

    const handleCancel = () => setOpen(false);

    const handleValidate = () => {
        onChange(pendingValues);
        setOpen(false);
    };

    const toggleOption = (optionName: string, checked: boolean) => {
        setPendingValues(prev => (checked ? [...prev, optionName] : prev.filter(v => v !== optionName)));
    };

    const toggleSelectAll = (allSelected: boolean) => {
        setPendingValues(allSelected ? [] : values.map(option => option.name));
    };

    const selectedLabels = committedValues.map(v => values.find(option => option.name === v)?.label ?? v);
    const selectedCountLabel = formatSelectedCount(t, committedValues.length);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, height: '100%', width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="caption" sx={{ color: '#495057', fontWeight: 600 }}>
                    {selectedCountLabel}
                </Typography>
                <Button size="small" variant="outlined" onClick={handleOpen} sx={{ textTransform: 'none' }}>
                    {t('pages.clients.queryBuilder.simple.selectionDialog.openButton')}
                </Button>
            </Box>

            <Box
                sx={{
                    flexGrow: 1,
                    minHeight: 150,
                    maxHeight: 220,
                    width: '100%',
                    overflowY: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    px: 1,
                    py: 0.5,
                }}
            >
                {selectedLabels.length === 0 ? (
                    <Typography variant="body2" sx={{ color: '#6c757d' }}>
                        {t('pages.clients.queryBuilder.simple.selectionDialog.noneSelected')}
                    </Typography>
                ) : (
                    selectedLabels.map((label, idx) => (
                        <Typography key={`${ruleId}-selected-${idx}`} variant="body2" sx={{ color: '#495057' }}>
                            {label}
                        </Typography>
                    ))
                )}
            </Box>

            <Dialog
                open={open}
                onClose={handleCancel}
                maxWidth="sm"
                fullWidth
                fullScreen={fullScreen}
                PaperProps={{ sx: { height: fullScreen ? '100%' : 640 } }}
            >
                <DialogTitle>{title}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column' }}>
                    <CheckboxListPicker
                        values={values}
                        selectedValues={pendingValues}
                        onToggleOption={toggleOption}
                        onToggleSelectAll={toggleSelectAll}
                        searchPlaceholder={t('pages.clients.queryBuilder.searchPlaceholder')}
                        selectAllLabel={t('pages.clients.queryBuilder.simple.selectAll')}
                        noOptionsLabel={t('pages.clients.queryBuilder.simple.noOptionsFound')}
                        selectedCountLabel={(count) => formatSelectedCount(t, count)}
                        fillHeight
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancel} sx={{ textTransform: 'none' }}>
                        {t('pages.clients.queryBuilder.simple.selectionDialog.cancel')}
                    </Button>
                    <Button onClick={handleValidate} variant="contained" sx={{ textTransform: 'none' }}>
                        {t('pages.clients.queryBuilder.simple.selectionDialog.validate')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default SelectionDialogEditor;
